/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { sharedAdminuiAppApi } from '../../../ServiceFactory';
import { getCurrentPage, getNextPage } from '../helpers';
import { useStore } from '../store';
import { ListType, Tenant } from '../types';

const useFetchTenants = (appVariantIds: string[], search?: string): UseInfiniteQueryResult<Tenant[]> => {
	const { setCurrentListPage, currentTenantsPage } = useStore();
	return useInfiniteQuery<Tenant[]>(
		[ListType.TENANT, { appVariantIds, search }],
		({ pageParam = 0 }) => {
			return sharedAdminuiAppApi()
				.getTenantsByContractOrAppVariant({ count: 50, appVariantIds, page: pageParam, search })
				.then(response => response)
				.then(data => {
					const { content, page } = data;
					const { totalPages, number: responsePage } = page || {};
					const processedItems = content?.map(item => item as Tenant) || [];
					const currentPage = getCurrentPage(responsePage, totalPages);
					setCurrentListPage(ListType.TENANT, currentPage);
					return processedItems;
				});
		},
		{
			getNextPageParam: () => getNextPage(currentTenantsPage)
		}
	);
};

export default useFetchTenants;
